import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../hooks/useTranslations';
import { translateCategory, translateCountry } from '../utils/translations';
import { getSensitiveTopicLabel } from '../utils/sensitiveTopicsTranslations';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { CRMTab } from '../components/admin/CRMTab';
import { CategoryManager } from '../components/admin/CategoryManager';
import { ProductImporter } from '../components/admin/ProductImporter';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import { ProductFormDialog } from '../components/admin/ProductFormDialog';
import SystemHealthDashboard from '../components/admin/SystemHealthDashboard';
import AuditLogDashboard from '../components/admin/AuditLogDashboard';
import FrontendErrorsDashboard from '../components/admin/FrontendErrorsDashboard';
import MediaSearchTab from '../components/admin/MediaSearchTab';
import GrowwerSyncTab from '../components/admin/GrowwerSyncTab';
import EmailToolTab from '../components/admin/EmailToolTab';
import SupervisorTab from '../components/admin/SupervisorTab';
import { ApiKeysTab } from '../components/admin/ApiKeysTab';
import { AdminProductsTable } from '../components/admin/AdminProductsTable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import {
  Users,
  Package,
  BarChart3,
  Plus,
  Pencil,
  Trash2,
  Shield,
  ShieldOff,
  UserX,
  UserCheck,
  Loader2,
  RefreshCw,
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Globe,
  FolderOpen,
  Languages,
  Search,
  X,
  History,
  Calendar,
  Filter,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  ShoppingCart,
  Check,
  RotateCcw,
  Mail,
  PenTool,
  CreditCard,
  DollarSign,
  Wand2,
  Clock,
  Settings,
  Activity,
  Bug,
  Scan,
  KeyRound,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const AdminPage = () => {
  const { user, isAdmin, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  
  // Visible columns state - hide less important columns by default
  const [visibleColumns, setVisibleColumns] = useState({
    website: true,
    email: true,
    country: true,
    pvp: true,
    pc: true,
    premiumV: true,
    premiumC: true,
    dr: true,
    traffic: true,
    available: true,
    linkType: true,
    duration: false,
    label: true,
    sensitive: true,
    infoPC: true,
    secondMail: true,
    deleteReason: false,
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  // Search state for products
  const [productSearch, setProductSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  
  // Admin filters state
  const [adminFilters, setAdminFilters] = useState({
    countries: [],
    categories: [],
    languages: [],
    drMin: '',
    drMax: '',
    trafficMin: '',
    trafficMax: '',
    priceMin: '',
    priceMax: '',
    // Temáticas sensibles
    casino: false,
    cbd: false,
    gambling: false,
    cryptocurrency: false,
    sex: false,
    erotic: false,
    vpn: false,
    tarot: false,
    porn: false,
    cannabis: false,
  });
  const [filterOptions, setFilterOptions] = useState({ countries: [], categories: [], languages: [] });
  const [countriesOpen, setCountriesOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [adminCountrySearch, setAdminCountrySearch] = useState('');
  const [adminCategorySearch, setAdminCategorySearch] = useState('');
  const [adminLanguageSearch, setAdminLanguageSearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [sensitiveSectionOpen, setSensitiveSectionOpen] = useState(false);
  
  // Pending users count
  const [pendingCustomersCount, setPendingCustomersCount] = useState(0);
  
  // Category management
  const [dbCategories, setDbCategories] = useState({ regular: [], sensitive: [] });
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState('regular');
  
  // Delete confirmation dialog
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Product selection state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [exportingSelected, setExportingSelected] = useState(false);

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatusMessage, setImportStatusMessage] = useState('');
  
  // Changes export state
  const [changeDays, setChangeDays] = useState(1);
  const [exportingChanges, setExportingChanges] = useState(false);

  // Excel tools loading states
  const [cleaningExcel, setCleaningExcel] = useState(false);
  const [detectingChanges, setDetectingChanges] = useState(false);
  
  // Background task states
  const [cleanExcelTaskId, setCleanExcelTaskId] = useState(null);
  const [detectChangesTaskId, setDetectChangesTaskId] = useState(null);
  const [taskProgress, setTaskProgress] = useState({ clean: 0, detect: 0 });
  const [taskMessage, setTaskMessage] = useState({ clean: '', detect: '' });
  
  // Refs to track if download already happened
  const cleanDownloadedRef = useRef(false);
  const detectDownloadedRef = useRef(false);
  
  // Poll for task status
  const pollTaskStatus = useCallback(async (taskId, taskType) => {
    // Check if already downloaded
    if (taskType === 'clean' && cleanDownloadedRef.current) return true;
    if (taskType === 'detect' && detectDownloadedRef.current) return true;
    
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/status`);
      const { status, progress, message, result_ready, error } = response.data;
      
      // Update progress
      setTaskProgress(prev => ({ ...prev, [taskType]: progress }));
      setTaskMessage(prev => ({ ...prev, [taskType]: message }));
      
      if (status === 'completed' && result_ready) {
        // Mark as downloaded BEFORE downloading to prevent duplicates
        if (taskType === 'clean') {
          if (cleanDownloadedRef.current) return true; // Already downloaded
          cleanDownloadedRef.current = true;
        } else {
          if (detectDownloadedRef.current) return true; // Already downloaded
          detectDownloadedRef.current = true;
        }
        
        // Download the result
        const downloadResponse = await axios.get(`${API}/tasks/${taskId}/download`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', taskType === 'clean' ? `productos_limpio_${Date.now()}.xlsx` : `cambios_detectados_${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success(language === 'es' ? '¡Proceso completado!' : 'Process completed!');
        
        // Reset states
        if (taskType === 'clean') {
          setCleaningExcel(false);
          setCleanExcelTaskId(null);
        } else {
          setDetectingChanges(false);
          setDetectChangesTaskId(null);
        }
        setTaskProgress(prev => ({ ...prev, [taskType]: 0 }));
        setTaskMessage(prev => ({ ...prev, [taskType]: '' }));
        
        return true; // Done
      } else if (status === 'failed') {
        toast.error(error || (language === 'es' ? 'Error en el proceso' : 'Process error'));
        if (taskType === 'clean') {
          setCleaningExcel(false);
          setCleanExcelTaskId(null);
          cleanDownloadedRef.current = false;
        } else {
          setDetectingChanges(false);
          setDetectChangesTaskId(null);
          detectDownloadedRef.current = false;
        }
        return true; // Done (with error)
      }
      
      return false; // Still running
    } catch (err) {
      console.error('Poll error:', err);
      return false;
    }
  }, [language]);
  
  // Effect to poll for clean excel task
  useEffect(() => {
    if (!cleanExcelTaskId) {
      cleanDownloadedRef.current = false; // Reset when no task
      return;
    }
    
    cleanDownloadedRef.current = false; // Reset for new task
    
    const interval = setInterval(async () => {
      const done = await pollTaskStatus(cleanExcelTaskId, 'clean');
      if (done) clearInterval(interval);
    }, 2000); // Poll every 2 seconds
    
    return () => clearInterval(interval);
  }, [cleanExcelTaskId, pollTaskStatus]);
  
  // Effect to poll for detect changes task
  useEffect(() => {
    if (!detectChangesTaskId) {
      detectDownloadedRef.current = false; // Reset when no task
      return;
    }
    
    detectDownloadedRef.current = false; // Reset for new task
    
    const interval = setInterval(async () => {
      const done = await pollTaskStatus(detectChangesTaskId, 'detect');
      if (done) clearInterval(interval);
    }, 2000); // Poll every 2 seconds
    
    return () => clearInterval(interval);
  }, [detectChangesTaskId, pollTaskStatus]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown="admin-countries"]') && !event.target.closest('[data-dropdown="admin-categories"]') && !event.target.closest('[data-dropdown="admin-languages"]')) {
        setCountriesOpen(false);
        setCategoriesOpen(false);
        setLanguagesOpen(false);
        setAdminCountrySearch('');
        setAdminCategorySearch('');
        setAdminLanguageSearch('');
      }
      if (!event.target.closest('[data-dropdown="column-selector"]')) {
        setShowColumnSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // User form state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    is_admin: false,
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Product form state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    website: '',
    country: '',
    category: '',
    casino: false,
    cbd: false,
    gambling: false,
    cryptocurrency: false,
    sex: false,
    erotic: false,
    vpn: false,
    tarot: false,
    porn: false,
    cannabis: false,
    ad_label: 'NO',
    pvp: 0,
    pc: 0,
    premium_sale_price: 0,
    premium_purchase_price: 0,
    more_info: '',
    available: true,
    dr: 0,
    organic_traffic: 0,
    language: '',
    site_type: '',
    link_type: 'Dofollow',
    link_duration: 'Permanente',
    web_email: '',
    mas_info_pc: '',
    second_email: '',
    motivo_eliminacion: '',
  });

  // Dropdown options
  const COUNTRIES = [
    // Europe
    'España', 'Francia', 'Alemania', 'Italia', 'Portugal', 'UK', 'Irlanda', 'Países Bajos', 'Bélgica', 'Suiza', 'Austria',
    'Polonia', 'Suecia', 'Noruega', 'Dinamarca', 'Finlandia', 'Grecia', 'República Checa', 'Hungría', 'Rumania', 'Bulgaria',
    'Croacia', 'Eslovaquia', 'Eslovenia', 'Serbia', 'Ucrania', 'Rusia', 'Turquía', 'Estonia', 'Letonia', 'Lituania',
    // Americas
    'USA', 'Canadá', 'México', 'Argentina', 'Brasil', 'Colombia', 'Chile', 'Perú', 'Venezuela', 'Ecuador', 'Bolivia',
    'Paraguay', 'Uruguay', 'Costa Rica', 'Panamá', 'Guatemala', 'Honduras', 'El Salvador', 'Nicaragua', 'Cuba',
    'República Dominicana', 'Puerto Rico', 'Jamaica',
    // Asia
    'China', 'Japón', 'Corea del Sur', 'India', 'Indonesia', 'Malasia', 'Singapur', 'Tailandia', 'Vietnam', 'Filipinas',
    'Pakistán', 'Bangladesh', 'Taiwán', 'Hong Kong', 'Israel', 'Emiratos Árabes', 'Arabia Saudita', 'Qatar', 'Kuwait',
    'Irán', 'Irak', 'Jordania', 'Líbano',
    // Africa
    'Marruecos', 'Egipto', 'Sudáfrica', 'Nigeria', 'Kenia', 'Ghana', 'Túnez', 'Argelia', 'Etiopía', 'Tanzania',
    // Oceania
    'Australia', 'Nueva Zelanda',
    // Special
    'Global', 'Internacional', 'Latinoamérica', 'Europa', 'Asia', 'África', 'Oriente Medio'
  ];
  const LANGUAGES = [
    // Major European
    'Español', 'English', 'Français', 'Deutsch', 'Italiano', 'Português', 'Nederlands', 'Polski', 'Русский',
    'Ελληνικά', 'Türkçe', 'Română', 'Magyar', 'Čeština', 'Svenska', 'Norsk', 'Dansk', 'Suomi', 'Українська',
    // Asian
    '日本語', '中文', '한국어', 'हिन्दी', 'العربية', 'עברית', 'ไทย', 'Tiếng Việt', 'Bahasa Indonesia', 'Bahasa Melayu',
    // Other
    'Català', 'Euskara', 'Galego'
  ];
  const LINK_TYPES = ['Dofollow', 'Nofollow', 'Sponsored'];
  const LINK_DURATIONS = ['Permanente', '1 Año', '2 Años', '3 Años'];
  const AD_LABELS = ['SI', 'NO'];
  const REGULAR_CATEGORIES = ['Generalista', 'Tecnología', 'Salud', 'Finanzas', 'Deportes', 'Viajes', 'Moda', 'Gastronomía', 'Motor', 'Inmobiliaria', 'Educación', 'Negocios', 'Entretenimiento', 'Lifestyle', 'Mascotas', 'Hogar', 'Belleza', 'Arte', 'Música', 'Cine'];
  const SENSITIVE_CATEGORIES = ['Casino', 'Apuestas', 'CBD', 'Criptomonedas', 'Adultos', 'Erótico', 'VPN', 'Tarot', 'Cannabis'];

  // Use centralized translations
  const t = useTranslations();
  
  // Build texts object from centralized translations for backwards compatibility
  const texts = {
    title: t.admin.title,
    stats: t.admin.stats,
    users: t.admin.users,
    products: t.admin.products,
    import: t.common.import,
    export: t.common.export,
    totalUsers: t.admin.totalUsers,
    adminUsers: t.admin.adminUsers,
    totalProducts: t.admin.totalProducts,
    activeProducts: t.admin.activeProducts,
    availableProducts: t.admin.availableProducts,
    uniqueCountries: t.admin.uniqueCountries,
    uniqueCategories: t.admin.uniqueCategories,
    name: t.common.name,
    email: t.common.email,
    role: language === 'es' ? 'Rol' : 'Role',
    status: t.common.status,
    actions: t.common.actions,
    admin: t.admin.users,
    user: language === 'es' ? 'Usuario' : 'User',
    active: t.common.active,
    inactive: t.common.inactive,
    makeAdmin: t.admin.makeAdmin,
    removeAdmin: t.admin.removeAdmin,
    activate: t.admin.activate,
    deactivate: t.admin.deactivate,
    delete: t.common.delete,
    deleteAll: t.admin.deleteAll,
    addProduct: t.products.addProduct,
    editProduct: t.products.editProduct,
    website: t.products.website,
    country: t.products.country,
    category: t.products.category,
    price: t.products.price,
    priceSensitive: t.products.priceSensitive,
    dr: t.products.dr,
    traffic: t.products.traffic,
    adLabel: t.products.adLabel,
    moreInfo: t.products.moreInfo,
    available: t.products.available,
    languageField: t.products.language,
    siteType: t.products.siteType,
    linkType: t.products.linkType,
    linkDuration: t.products.linkDuration,
    save: t.common.save,
    cancel: t.common.cancel,
    confirmDelete: t.admin.confirmDelete,
    confirmDeleteAll: t.admin.confirmDeleteAll,
    importTitle: t.import.title,
    importDescription: t.import.description,
    selectFile: t.import.selectFile,
    replaceAll: t.import.replaceAll,
    replaceAllWarning: t.import.replaceAllWarning,
    downloadTemplate: t.import.downloadTemplate,
    importButton: t.import.importButton,
    importing: t.import.importing,
    importSuccess: t.import.importSuccess,
    importedRows: t.import.importedRows,
    errorRows: t.import.errorRows,
    exportAll: t.products.exportAll,
    exportFiltered: t.products.exportFiltered,
    exportPage: language === 'es' ? 'Exportar Página' : 'Export Page',
    page: t.common.page,
    of: t.common.of,
    showing: t.common.showing,
    noProducts: t.products.noProducts,
    searchPlaceholder: t.products.searchPlaceholder,
    search: t.common.search,
    clearSearch: language === 'es' ? 'Limpiar' : 'Clear',
    exportChanges: t.products.exportChanges,
    exportingChanges: language === 'es' ? 'Descargando...' : 'Downloading...',
    days: t.products.days,
    today: t.products.today,
    lastDays: t.products.lastDays,
    filterByCountry: t.products.filterByCountry,
    filterByCategory: t.products.filterByCategory,
    allCountries: t.products.allCountries,
    allCategories: t.products.allCategories,
    drMin: t.products.drMin,
    drMax: t.products.drMax,
    trafficMin: t.products.trafficMin,
    trafficMax: t.products.trafficMax,
    priceMin: t.products.priceMin,
    priceMax: t.products.priceMax,
    confirmDeleteAllTitle: language === 'es' ? '¿Eliminar TODOS los productos?' : 'Delete ALL products?',
    confirmDeleteAllDesc: language === 'es' 
      ? 'Esta acción no se puede deshacer. Se eliminarán todos los productos de la base de datos.'
      : 'This action cannot be undone. All products will be deleted from the database.',
    typeToConfirm: t.admin.typeToConfirm,
    deleteWord: t.admin.deleteWord,
    selectAll: t.products.selectAll,
    deselectAll: t.products.deselectAll,
    selected: t.products.selected,
    exportSelected: t.products.exportSelected,
    noSelection: t.products.noSelection,
    addUser: t.admin.addUser,
    createUser: t.admin.createUser,
    password: t.auth.password,
    isAdmin: t.admin.isAdmin,
    userCreated: t.admin.userCreated,
    userCreateError: t.admin.userCreateError,
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const refreshPendingCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/pending-count`);
      setPendingCustomersCount(res.data.pending_count || 0);
    } catch (error) {
      console.error('Error refreshing pending count:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/customers/pending-count`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPendingCustomersCount(pendingRes.data.pending_count || 0);
      await fetchProducts(1);
      await fetchFilterOptions();
      await fetchCategories();
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (page = 1, search = productSearch, filters = adminFilters, sort = sortField, order = sortOrder, email = emailSearch) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (search) params.append('search', search);
      if (email) params.append('email', email);
      if (filters.countries && filters.countries.length > 0) {
        filters.countries.forEach(c => params.append('country', c));
      }
      if (filters.categories && filters.categories.length > 0) {
        filters.categories.forEach(c => params.append('category', c));
      }
      if (filters.languages && filters.languages.length > 0) {
        filters.languages.forEach(l => params.append('language', l));
      }
      if (filters.drMin) params.append('dr_min', filters.drMin);
      if (filters.drMax) params.append('dr_max', filters.drMax);
      if (filters.trafficMin) params.append('traffic_min', filters.trafficMin);
      if (filters.trafficMax) params.append('traffic_max', filters.trafficMax);
      if (filters.priceMin) params.append('price_min', filters.priceMin);
      if (filters.priceMax) params.append('price_max', filters.priceMax);
      // Temáticas sensibles
      if (filters.casino) params.append('casino', 'true');
      if (filters.cbd) params.append('cbd', 'true');
      if (filters.gambling) params.append('gambling', 'true');
      if (filters.cryptocurrency) params.append('cryptocurrency', 'true');
      if (filters.sex) params.append('sex', 'true');
      if (filters.erotic) params.append('erotic', 'true');
      if (filters.vpn) params.append('vpn', 'true');
      if (filters.tarot) params.append('tarot', 'true');
      if (filters.porn) params.append('porn', 'true');
      if (filters.cannabis) params.append('cannabis', 'true');
      if (sort) {
        params.append('sort_by', sort);
        params.append('sort_order', order);
      }
      
      const response = await axios.get(`${API}/admin/products?${params.toString()}`);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Category management functions
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      const cats = response.data || [];
      setDbCategories({
        regular: cats.filter(c => c.type === 'regular'),
        sensitive: cats.filter(c => c.type === 'sensitive')
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Nombre de categoría es obligatorio');
      return;
    }
    try {
      await axios.post(`${API}/admin/categories`, { name: newCategoryName, type: newCategoryType });
      toast.success('Categoría creada');
      setNewCategoryName('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando categoría');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando categoría');
    }
  };

  const handleSeedCategories = async () => {
    try {
      const response = await axios.post(`${API}/admin/categories/seed`);
      toast.success(response.data.message);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };
  
  const handleSort = (field) => {
    let newOrder = 'asc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortOrder(newOrder);
    fetchProducts(1, productSearch, adminFilters, field, newOrder);
  };
  
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters/options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };
  
  // Auto-search with debounce for product search and email search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(1, productSearch, adminFilters, sortField, sortOrder, emailSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [productSearch, emailSearch]);

  const handleProductSearch = (e) => {
    e.preventDefault();
    fetchProducts(1, productSearch, adminFilters, sortField, sortOrder, emailSearch);
  };
  
  const handleClearFilters = () => {
    setProductSearch('');
    setEmailSearch('');
    const emptyFilters = { 
      countries: [], categories: [], languages: [], drMin: '', drMax: '', trafficMin: '', trafficMax: '', priceMin: '', priceMax: '',
      casino: false, cbd: false, gambling: false, cryptocurrency: false, sex: false, erotic: false, vpn: false, tarot: false, porn: false, cannabis: false
    };
    setAdminFilters(emptyFilters);
    fetchProducts(1, '', emptyFilters, sortField, sortOrder, '');
  };
  
  const toggleSensitiveFilter = (topic) => {
    setAdminFilters(prev => {
      const newFilters = { ...prev, [topic]: !prev[topic] };
      fetchProducts(1, productSearch, newFilters);
      return newFilters;
    });
  };
  
  const toggleCountry = (country) => {
    setAdminFilters(prev => {
      const newCountries = prev.countries.includes(country)
        ? prev.countries.filter(c => c !== country)
        : [...prev.countries, country];
      const newFilters = { ...prev, countries: newCountries };
      fetchProducts(1, productSearch, newFilters);
      return newFilters;
    });
  };

  const toggleCategory = (category) => {
    setAdminFilters(prev => {
      const newCategories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      const newFilters = { ...prev, categories: newCategories };
      fetchProducts(1, productSearch, newFilters);
      return newFilters;
    });
  };

  const toggleLanguage = (lang) => {
    setAdminFilters(prev => {
      const newLanguages = prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang];
      const newFilters = { ...prev, languages: newLanguages };
      fetchProducts(1, productSearch, newFilters);
      return newFilters;
    });
  };

  const handleDownloadCatalog = async (filtered = false) => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filtered) {
        if (adminFilters.countries.length > 0) {
          adminFilters.countries.forEach(c => params.append('country', c));
        }
        if (adminFilters.categories.length > 0) {
          adminFilters.categories.forEach(c => params.append('category', c));
        }
        if (adminFilters.languages && adminFilters.languages.length > 0) {
          adminFilters.languages.forEach(l => params.append('language', l));
        }
        if (adminFilters.drMin) params.append('dr_min', adminFilters.drMin);
        if (adminFilters.drMax) params.append('dr_max', adminFilters.drMax);
        if (adminFilters.trafficMin) params.append('traffic_min', adminFilters.trafficMin);
        if (adminFilters.trafficMax) params.append('traffic_max', adminFilters.trafficMax);
        if (adminFilters.priceMin) params.append('price_min', adminFilters.priceMin);
        if (adminFilters.priceMax) params.append('price_max', adminFilters.priceMax);
        if (productSearch) params.append('search', productSearch);
      }
      
      const response = await axios.get(`${API}/products/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filtered ? 'productos_filtrados.xlsx' : 'catalogo_completo.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(filtered ? 'Productos filtrados descargados' : 'Catálogo completo descargado');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar');
    } finally {
      setDownloading(false);
    }
  };

  const handleAdminFilterChange = (key, value) => {
    const newFilters = { ...adminFilters, [key]: value };
    setAdminFilters(newFilters);
    fetchProducts(1, productSearch, newFilters);
  };

  const handleUserUpdate = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      toast.success('User updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleUserDelete = async (userId) => {
    if (!window.confirm(texts.confirmDelete)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, userForm);
      toast.success(texts.userCreated);
      setUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', is_admin: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || texts.userCreateError);
    } finally {
      setCreatingUser(false);
    }
  };

  // Clean website URL (remove http, https, www, trailing slashes)
  const cleanWebsiteUrl = (url) => {
    if (!url) return '';
    return url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '')
      .toLowerCase()
      .trim();
  };

  // Validate product form
  const validateProductForm = () => {
    const errors = [];
    if (!productForm.website.trim()) errors.push('Website es obligatorio');
    if (!productForm.country) errors.push('País es obligatorio');
    if (!productForm.language) errors.push('Idioma es obligatorio');
    if (!productForm.link_type) errors.push('Tipo de enlace es obligatorio');
    if (!productForm.link_duration) errors.push('Duración es obligatorio');
    if (!productForm.ad_label) errors.push('Etiqueta es obligatorio');
    if (!productForm.site_type) errors.push('Tipo de medio es obligatorio');
    if (productForm.pvp <= 0) errors.push('PVP debe ser mayor a 0');
    if (productForm.pc <= 0) errors.push('PC (Precio Compra) debe ser mayor a 0');
    return errors;
  };

  const handleProductSave = async () => {
    // Clean website URL
    const cleanedWebsite = cleanWebsiteUrl(productForm.website);
    const formToSave = { ...productForm, website: cleanedWebsite };

    // Validate
    const errors = validateProductForm();
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, formToSave);
        toast.success('Producto actualizado');
      } else {
        // Check if website already exists
        const existingCheck = await axios.get(`${API}/products?search=${cleanedWebsite}&limit=1`);
        if (existingCheck.data.products?.length > 0 && 
            existingCheck.data.products[0].website.toLowerCase() === cleanedWebsite) {
          toast.error('Este website ya existe en el catálogo');
          return;
        }
        await axios.post(`${API}/admin/products`, formToSave);
        toast.success('Producto creado');
      }
      setProductDialogOpen(false);
      setEditingProduct(null);
      resetProductForm();
      fetchProducts(pagination.page);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando producto');
    }
  };

  const handleProductDelete = async (productId) => {
    if (!window.confirm(texts.confirmDelete)) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts(pagination.page);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting product');
    }
  };

  // Delete selected products
  const handleDeleteSelectedProducts = async () => {
    if (selectedProducts.size === 0) {
      toast.error(language === 'es' ? 'No hay productos seleccionados' : 'No products selected');
      return;
    }
    
    const confirmMsg = language === 'es' 
      ? `¿Estás seguro de eliminar ${selectedProducts.size} productos seleccionados?`
      : `Are you sure you want to delete ${selectedProducts.size} selected products?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await axios.post(`${API}/admin/products/delete-selected`, {
        product_ids: Array.from(selectedProducts)
      });
      toast.success(response.data.message);
      setSelectedProducts(new Set());
      fetchProducts(pagination.page);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting products');
    }
  };

  const handleDeleteAllProducts = async () => {
    if (deleteConfirmText !== 'ELIMINAR') {
      toast.error(language === 'es' ? 'Escribe ELIMINAR para confirmar' : 'Type DELETE to confirm');
      return;
    }
    try {
      const response = await axios.delete(`${API}/admin/products`);
      toast.success(response.data.message);
      setDeleteAllDialogOpen(false);
      setDeleteConfirmText('');
      fetchProducts(1);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting products');
    }
  };

  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        website: product.website || '',
        country: product.country || '',
        category: product.category || '',
        casino: product.casino || false,
        cbd: product.cbd || false,
        gambling: product.gambling || false,
        cryptocurrency: product.cryptocurrency || false,
        sex: product.sex || false,
        erotic: product.erotic || false,
        vpn: product.vpn || false,
        tarot: product.tarot || false,
        porn: product.porn || false,
        cannabis: product.cannabis || false,
        ad_label: product.ad_label || '',
        pvp: product.pvp || 0,
        pc: product.pc || 0,
        premium_sale_price: product.premium_sale_price || 0,
        premium_purchase_price: product.premium_purchase_price || 0,
        more_info: product.more_info || '',
        available: product.available !== false,
        dr: product.dr || 0,
        organic_traffic: product.organic_traffic || 0,
        language: product.language || '',
        site_type: product.site_type || '',
        link_type: product.link_type || '',
        link_duration: product.link_duration || '',
        web_email: product.web_email || '',
        mas_info_pc: product.mas_info_pc || '',
        second_email: product.second_email || '',
        motivo_eliminacion: product.motivo_eliminacion || '',
      });
    } else {
      setEditingProduct(null);
      resetProductForm();
    }
    setProductDialogOpen(true);
  };

  const resetProductForm = () => {
    setProductForm({
      website: '',
      country: '',
      category: '',
      casino: false,
      cbd: false,
      gambling: false,
      cryptocurrency: false,
      sex: false,
      erotic: false,
      vpn: false,
      tarot: false,
      porn: false,
      cannabis: false,
      ad_label: 'NO',
      pvp: 0,
      pc: 0,
      premium_sale_price: 0,
      premium_purchase_price: 0,
      more_info: '',
      available: true,
      dr: 0,
      organic_traffic: 0,
      language: '',
      site_type: '',
      link_type: 'Dofollow',
      link_duration: 'Permanente',
      web_email: '',
      mas_info_pc: '',
      second_email: '',
      motivo_eliminacion: '',
    });
  };

  // Import/Export functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    setImportResult(null);
    setImportProgress(0);
    setImportStatusMessage(language === 'es' ? 'Subiendo archivo...' : 'Uploading file...');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const authToken = token || localStorage.getItem('auth_token');
      // Start background import (does not time out for large files)
      const startRes = await axios.post(
        `${API}/admin/products/import-async?replace_all=${replaceAll}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        throw new Error('No job_id returned from server');
      }
      setImportStatusMessage(language === 'es' ? 'Procesando en segundo plano...' : 'Processing in background...');

      // Poll for status
      let done = false;
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
        // eslint-disable-next-line no-await-in-loop
        const statusRes = await axios.get(`${API}/admin/products/import-status/${jobId}`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        const data = statusRes.data || {};
        setImportProgress(data.progress || 0);
        if (data.status === 'processing') {
          setImportStatusMessage(
            `${language === 'es' ? 'Procesando' : 'Processing'} ${data.imported || 0}/${data.total_rows || '?'}`
          );
        } else if (data.status === 'pending') {
          setImportStatusMessage(language === 'es' ? 'Iniciando...' : 'Starting...');
        } else if (data.status === 'completed') {
          setImportResult({
            total_rows: data.total_rows,
            imported: data.imported,
            errors: data.errors,
            error_details: data.error_details || [],
          });
          toast.success(`${data.imported} ${texts.importedRows}`);
          fetchProducts(1);
          fetchData();
          done = true;
        } else if (data.status === 'failed') {
          const msg = data.error || (data.error_details && data.error_details[0]) || 'Import failed';
          throw new Error(msg);
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Error importing file';
      toast.error(msg);
      setImportResult({
        total_rows: 0,
        imported: 0,
        errors: 1,
        error_details: [msg],
      });
    } finally {
      setImporting(false);
      setImportStatusMessage('');
    }
  };

  const handleExport = async (exportAll = true, exportFiltered = false) => {
    try {
      const params = new URLSearchParams();
      params.append('format', 'xlsx');
      
      if (exportFiltered) {
        // Export with current filters
        if (productSearch) params.append('search', productSearch);
        if (adminFilters.country && adminFilters.country !== 'all') params.append('country', adminFilters.country);
        if (adminFilters.category && adminFilters.category !== 'all') params.append('category', adminFilters.category);
        if (adminFilters.drMin) params.append('dr_min', adminFilters.drMin);
        if (adminFilters.drMax) params.append('dr_max', adminFilters.drMax);
        if (adminFilters.trafficMin) params.append('traffic_min', adminFilters.trafficMin);
        if (adminFilters.trafficMax) params.append('traffic_max', adminFilters.trafficMax);
        if (adminFilters.priceMin) params.append('price_min', adminFilters.priceMin);
        if (adminFilters.priceMax) params.append('price_max', adminFilters.priceMax);
      } else if (!exportAll) {
        // Export current page only
        params.append('page', pagination.page);
        params.append('limit', 50);
      }

      const response = await axios.get(`${API}/admin/products/export?${params.toString()}`, { responseType: 'blob' });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `productos_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();

      toast.success('Export completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error exporting');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/template`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'plantilla_productos.xlsx';
      link.click();
    } catch (error) {
      toast.error('Error downloading template');
    }
  };

  const handleExportChanges = async () => {
    setExportingChanges(true);
    try {
      const response = await axios.get(`${API}/admin/products/changes/export?days=${changeDays}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `cambios_productos_${changeDays}_dias.xlsx`;
      link.click();
      toast.success(language === 'es' ? 'Cambios descargados' : 'Changes downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error exporting changes');
    } finally {
      setExportingChanges(false);
    }
  };

  // Product selection functions
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  // Select ALL products from database (not just current page)
  const handleSelectAllFromDB = async () => {
    try {
      // Build params with current filters
      const params = new URLSearchParams();
      if (productSearch) params.append('search', productSearch);
      if (adminFilters.country && adminFilters.country !== 'all') params.append('country', adminFilters.country);
      if (adminFilters.category && adminFilters.category !== 'all') params.append('category', adminFilters.category);
      if (adminFilters.drMin) params.append('dr_min', adminFilters.drMin);
      if (adminFilters.drMax) params.append('dr_max', adminFilters.drMax);
      if (adminFilters.trafficMin) params.append('traffic_min', adminFilters.trafficMin);
      if (adminFilters.trafficMax) params.append('traffic_max', adminFilters.trafficMax);
      if (adminFilters.priceMin) params.append('price_min', adminFilters.priceMin);
      if (adminFilters.priceMax) params.append('price_max', adminFilters.priceMax);
      
      const response = await axios.get(`${API}/admin/products/all-ids?${params.toString()}`);
      const allIds = response.data.ids || [];
      setSelectedProducts(new Set(allIds));
      toast.success(language === 'es' ? `${allIds.length} productos seleccionados` : `${allIds.length} products selected`);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al seleccionar todos' : 'Error selecting all');
    }
  };

  const handleExportSelected = async () => {
    if (selectedProducts.size === 0) {
      toast.error(texts.noSelection);
      return;
    }
    
    setExportingSelected(true);
    try {
      const response = await axios.post(
        `${API}/admin/products/export-selected`,
        Array.from(selectedProducts),
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `productos_seleccionados_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(language === 'es' ? 'Exportación completada' : 'Export completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error exporting selected products');
    } finally {
      setExportingSelected(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-display font-bold text-foreground">{texts.title}</h1>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 rounded-xl w-fit mb-8">
            <Button
              variant={activeTab === 'stats' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('stats')}
              className={activeTab === 'stats' ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-violet-600 hover:bg-white font-medium'}
            >
              <BarChart3 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{texts.stats}</span>
            </Button>
            <Button
              variant={activeTab === 'users' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('users')}
              className={`relative ${activeTab === 'users' ? 'bg-sky-600 hover:bg-sky-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-sky-600 hover:bg-white font-medium'}`}
            >
              <Users className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{texts.users}</span>
            </Button>
            <Button
              variant={activeTab === 'products' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('products')}
              className={activeTab === 'products' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-emerald-600 hover:bg-white font-medium'}
            >
              <Package className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{texts.products}</span>
            </Button>
            <Button
              variant={activeTab === 'orders' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('orders')}
              className={activeTab === 'orders' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-indigo-600 hover:bg-white font-medium'}
            >
              <ShoppingCart className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Pedidos' : 'Orders'}</span>
            </Button>
            <Button
              variant={activeTab === 'customers' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('customers')}
              className={`relative ${activeTab === 'customers' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-purple-600 hover:bg-white font-medium'}`}
            >
              <UserCheck className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Clientes' : 'Customers'}</span>
              {pendingCustomersCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {pendingCustomersCount}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === 'writers' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('writers')}
              className={activeTab === 'writers' ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-teal-600 hover:bg-white font-medium'}
            >
              <PenTool className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Redactores' : 'Writers'}</span>
            </Button>
            <Button
              variant={activeTab === 'expenses' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('expenses')}
              className={activeTab === 'expenses' ? 'bg-red-600 hover:bg-red-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-red-600 hover:bg-white font-medium'}
            >
              <CreditCard className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Gastos' : 'Expenses'}</span>
            </Button>
            <Button
              variant={activeTab === 'income' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('income')}
              className={activeTab === 'income' ? 'bg-green-600 hover:bg-green-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-green-600 hover:bg-white font-medium'}
            >
              <DollarSign className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Ingresos' : 'Income'}</span>
            </Button>
            <Button
              variant={activeTab === 'accounting' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('accounting')}
              className={activeTab === 'accounting' ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-blue-600 hover:bg-white font-medium'}
            >
              <BarChart3 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Contabilidad' : 'Accounting'}</span>
            </Button>
            <Button
              variant={activeTab === 'import' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('import')}
              className={activeTab === 'import' ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-amber-600 hover:bg-white font-medium'}
            >
              <Upload className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{texts.import}</span>
            </Button>
            <Button
              variant={activeTab === 'categories' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('categories')}
              className={activeTab === 'categories' ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-orange-600 hover:bg-white font-medium'}
            >
              <FolderOpen className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Categorías' : 'Categories'}</span>
            </Button>
            <Button
              variant={activeTab === 'analytics' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('analytics')}
              className={activeTab === 'analytics' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-indigo-600 hover:bg-white font-medium'}
            >
              <BarChart3 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Analíticas' : 'Analytics'}</span>
            </Button>
            <Button
              variant={activeTab === 'health' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('health')}
              className={activeTab === 'health' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-emerald-600 hover:bg-white font-medium'}
            >
              <Activity className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Estado' : 'Health'}</span>
            </Button>
            <Button
              variant={activeTab === 'audit' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('audit')}
              className={activeTab === 'audit' ? 'bg-slate-600 hover:bg-slate-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-slate-700 hover:bg-white font-medium'}
            >
              <History className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Auditoría' : 'Audit'}</span>
            </Button>
            <Button
              variant={activeTab === 'errors' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('errors')}
              className={activeTab === 'errors' ? 'bg-red-600 hover:bg-red-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-red-600 hover:bg-white font-medium'}
            >
              <Bug className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Errores' : 'Errors'}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('media-search')}
              className={activeTab === 'media-search' ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-cyan-600 hover:bg-white font-medium'}
            >
              <Search className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'es' ? 'Buscar Medios' : 'Media Search'}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('growwer')}
              className={activeTab === 'growwer' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-purple-600 hover:bg-white font-medium'}
            >
              <RefreshCw className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Growwer</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('email-tool')}
              className={activeTab === 'email-tool' ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-teal-600 hover:bg-white font-medium'}
              data-testid="email-tool-tab"
            >
              <Mail className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Email</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('supervisor')}
              className={activeTab === 'supervisor' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-indigo-600 hover:bg-white font-medium'}
              data-testid="supervisor-tab"
            >
              <Scan className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Supervisor</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('api-keys')}
              className={activeTab === 'api-keys' ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-amber-600 hover:bg-white font-medium'}
              data-testid="api-keys-tab"
            >
              <KeyRound className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">API Keys</span>
            </Button>
          </div>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{texts.totalUsers}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-primary" />
                    <span className="text-2xl font-bold">{stats?.total_users || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{texts.adminUsers}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Shield className="h-6 w-6 text-accent" />
                    <span className="text-2xl font-bold">{stats?.admin_users || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{texts.totalProducts}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Package className="h-6 w-6 text-mint-dark" />
                    <span className="text-2xl font-bold">{stats?.total_products?.toLocaleString() || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{texts.availableProducts}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-6 w-6 text-success" />
                    <span className="text-2xl font-bold">{stats?.available_products?.toLocaleString() || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{texts.uniqueCountries}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Globe className="h-6 w-6 text-primary" />
                    <span className="text-2xl font-bold">{stats?.unique_countries || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{texts.uniqueCategories}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-6 w-6 text-secondary" />
                    <span className="text-2xl font-bold">{stats?.unique_categories || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{texts.users}</CardTitle>
                <Button onClick={() => setUserDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="h-4 w-4 mr-2" />
                  {texts.addUser}
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.name}</TableHead>
                      <TableHead>{texts.email}</TableHead>
                      <TableHead>{texts.role}</TableHead>
                      <TableHead>{texts.status}</TableHead>
                      <TableHead className="text-right">{texts.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Badge variant={u.is_admin ? 'default' : 'secondary'}>
                            {u.is_admin ? texts.admin : texts.user}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.is_active ? 'outline' : 'destructive'}>
                            {u.is_active ? texts.active : texts.inactive}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {u.id !== user?.id && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUserUpdate(u.id, { is_admin: !u.is_admin })}
                                  title={u.is_admin ? texts.removeAdmin : texts.makeAdmin}
                                >
                                  {u.is_admin ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUserUpdate(u.id, { is_active: !u.is_active })}
                                  title={u.is_active ? texts.deactivate : texts.activate}
                                >
                                  {u.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUserDelete(u.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            {/* Search Bar */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <form onSubmit={handleProductSearch} className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={texts.searchPlaceholder}
                      value={productSearch}
                      onChange={(e) => setProductSearch(cleanWebsiteUrl(e.target.value))}
                      className="pl-10"
                    />
                  </div>
                  <div className="relative flex-1 max-w-xs">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={language === 'es' ? 'Buscar por email...' : 'Search by email...'}
                      value={emailSearch}
                      onChange={(e) => setEmailSearch(e.target.value.toLowerCase().trim())}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 sm:flex-none">
                      <Search className="h-4 w-4 mr-2" />
                      {texts.search}
                    </Button>
                    {(productSearch || emailSearch || adminFilters.countries.length > 0 || adminFilters.categories.length > 0 || adminFilters.drMin || adminFilters.drMax || adminFilters.priceMin || adminFilters.priceMax || adminFilters.trafficMin || adminFilters.trafficMax) && (
                      <Button type="button" variant="destructive" onClick={handleClearFilters} className="bg-red-600 hover:bg-red-700">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Limpiar Filtros' : 'Clear Filters'}
                      </Button>
                    )}
                  </div>
                </form>
                
                {/* Filters Row */}
                <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t">
                  {/* Country Multi-Select */}
                  <div className="flex items-center gap-2" data-dropdown="admin-countries">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <div className="relative">
                      <button
                        onClick={() => { setCountriesOpen(!countriesOpen); setCategoriesOpen(false); }}
                        className="flex items-center justify-between px-3 py-2 h-10 w-[180px] border border-input rounded-md bg-background hover:bg-emerald-50 hover:border-emerald-300 text-sm"
                      >
                        <span className="truncate">
                          {adminFilters.countries.length === 0 
                            ? texts.allCountries 
                            : adminFilters.countries.length === 1 
                              ? translateCountry(adminFilters.countries[0], language)
                              : `${adminFilters.countries.length} países`
                          }
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${countriesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {countriesOpen && (
                        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-hidden bg-white border border-border rounded-md shadow-lg">
                          <div className="p-2 border-b sticky top-0 bg-white">
                            <Input
                              placeholder={language === 'es' ? 'Buscar país...' : 'Search country...'}
                              value={adminCountrySearch}
                              onChange={(e) => setAdminCountrySearch(e.target.value)}
                              className="h-8 text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="p-2 max-h-52 overflow-auto">
                            {adminFilters.countries.length > 0 && (
                              <button
                                onClick={() => setAdminFilters(prev => { const newFilters = { ...prev, countries: [] }; fetchProducts(1, productSearch, newFilters); return newFilters; })}
                                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2 border-b mb-2"
                              >
                                <RotateCcw className="h-3 w-3" />
                                {language === 'es' ? 'Limpiar selección' : 'Clear selection'}
                              </button>
                            )}
                            {!adminCountrySearch && (
                              <button
                                onClick={() => setAdminFilters(prev => { const newFilters = { ...prev, countries: [] }; fetchProducts(1, productSearch, newFilters); return newFilters; })}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                              >
                                <Checkbox checked={adminFilters.countries.length === 0} className="pointer-events-none" />
                                {texts.allCountries}
                              </button>
                            )}
                            {filterOptions.countries
                              .filter(country => 
                                !adminCountrySearch || 
                                country.toLowerCase().includes(adminCountrySearch.toLowerCase()) ||
                                translateCountry(country, language).toLowerCase().includes(adminCountrySearch.toLowerCase())
                              )
                              .map(country => (
                              <button
                                key={country}
                                onClick={() => toggleCountry(country)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                              >
                                <Checkbox checked={adminFilters.countries.includes(country)} className="pointer-events-none" />
                                {translateCountry(country, language)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Category Multi-Select */}
                  <div className="flex items-center gap-2" data-dropdown="admin-categories">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <div className="relative">
                      <button
                        onClick={() => { setCategoriesOpen(!categoriesOpen); setCountriesOpen(false); }}
                        className="flex items-center justify-between px-3 py-2 h-10 w-[180px] border border-input rounded-md bg-background hover:bg-emerald-50 hover:border-emerald-300 text-sm"
                      >
                        <span className="truncate">
                          {adminFilters.categories.length === 0 
                            ? texts.allCategories 
                            : adminFilters.categories.length === 1 
                              ? translateCategory(adminFilters.categories[0], language)
                              : `${adminFilters.categories.length} categorías`
                          }
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {categoriesOpen && (
                        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-hidden bg-white border border-border rounded-md shadow-lg">
                          <div className="p-2 border-b sticky top-0 bg-white">
                            <Input
                              placeholder={language === 'es' ? 'Buscar categoría...' : 'Search category...'}
                              value={adminCategorySearch}
                              onChange={(e) => setAdminCategorySearch(e.target.value)}
                              className="h-8 text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="p-2 max-h-52 overflow-auto">
                            {adminFilters.categories.length > 0 && (
                              <button
                                onClick={() => setAdminFilters(prev => { const newFilters = { ...prev, categories: [] }; fetchProducts(1, productSearch, newFilters); return newFilters; })}
                                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2 border-b mb-2"
                              >
                                <RotateCcw className="h-3 w-3" />
                                {language === 'es' ? 'Limpiar selección' : 'Clear selection'}
                              </button>
                            )}
                            {!adminCategorySearch && (
                              <button
                                onClick={() => setAdminFilters(prev => { const newFilters = { ...prev, categories: [] }; fetchProducts(1, productSearch, newFilters); return newFilters; })}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                              >
                                <Checkbox checked={adminFilters.categories.length === 0} className="pointer-events-none" />
                                {texts.allCategories}
                              </button>
                            )}
                            {filterOptions.categories
                              .filter(category => 
                                !adminCategorySearch || 
                                category.toLowerCase().includes(adminCategorySearch.toLowerCase()) ||
                                translateCategory(category, language).toLowerCase().includes(adminCategorySearch.toLowerCase())
                              )
                              .map(category => (
                              <button
                                key={category}
                                onClick={() => toggleCategory(category)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                              >
                                <Checkbox checked={adminFilters.categories.includes(category)} className="pointer-events-none" />
                                {translateCategory(category, language)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Language Multi-Select */}
                  <div className="flex items-center gap-2" data-dropdown="admin-languages">
                    <Languages className="h-4 w-4 text-muted-foreground" />
                    <div className="relative">
                      <button
                        onClick={() => { setLanguagesOpen(!languagesOpen); setCountriesOpen(false); setCategoriesOpen(false); }}
                        className="flex items-center justify-between px-3 py-2 h-10 w-[180px] border border-input rounded-md bg-background hover:bg-emerald-50 hover:border-emerald-300 text-sm"
                        data-testid="admin-language-filter"
                      >
                        <span className="truncate">
                          {adminFilters.languages.length === 0
                            ? (language === 'es' ? 'Todos los idiomas' : 'All languages')
                            : adminFilters.languages.length === 1
                              ? adminFilters.languages[0]
                              : `${adminFilters.languages.length} ${language === 'es' ? 'idiomas' : 'languages'}`
                          }
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${languagesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {languagesOpen && (
                        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-hidden bg-white border border-border rounded-md shadow-lg">
                          <div className="p-2 border-b sticky top-0 bg-white">
                            <Input
                              placeholder={language === 'es' ? 'Buscar idioma...' : 'Search language...'}
                              value={adminLanguageSearch}
                              onChange={(e) => setAdminLanguageSearch(e.target.value)}
                              className="h-8 text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="p-2 max-h-52 overflow-auto">
                            {adminFilters.languages.length > 0 && (
                              <button
                                onClick={() => setAdminFilters(prev => { const newFilters = { ...prev, languages: [] }; fetchProducts(1, productSearch, newFilters); return newFilters; })}
                                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2 border-b mb-2"
                              >
                                <RotateCcw className="h-3 w-3" />
                                {language === 'es' ? 'Limpiar selección' : 'Clear selection'}
                              </button>
                            )}
                            {(filterOptions.languages || [])
                              .filter(l => !adminLanguageSearch || l.toLowerCase().includes(adminLanguageSearch.toLowerCase()))
                              .map(lang => (
                                <button
                                  key={lang}
                                  onClick={() => toggleLanguage(lang)}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                                >
                                  <Checkbox checked={adminFilters.languages.includes(lang)} className="pointer-events-none" />
                                  {lang}
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DR Range Filter */}
                  <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-950 px-3 py-1 rounded-lg border border-blue-200 dark:border-blue-800">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">DR:</span>
                    <Input
                      type="number"
                      placeholder="0"
                      value={adminFilters.drMin}
                      onChange={(e) => {
                        const val = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                        handleAdminFilterChange('drMin', val === 0 ? '' : String(val));
                      }}
                      className="w-[60px] h-8 text-center bg-white dark:bg-gray-900"
                      min="0"
                      max="100"
                    />
                    <span className="text-blue-500">-</span>
                    <Input
                      type="number"
                      placeholder="100"
                      value={adminFilters.drMax}
                      onChange={(e) => {
                        const val = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                        handleAdminFilterChange('drMax', val === 0 ? '' : String(val));
                      }}
                      className="w-[60px] h-8 text-center bg-white dark:bg-gray-900"
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  {/* Price Range Filter */}
                  <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950 px-3 py-1 rounded-lg border border-green-200 dark:border-green-800">
                    <span className="text-sm font-medium text-green-700 dark:text-green-300">€ Precio:</span>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={adminFilters.priceMin}
                      onChange={(e) => handleAdminFilterChange('priceMin', e.target.value)}
                      className="w-[70px] h-8 text-center bg-white dark:bg-gray-900"
                      min="0"
                    />
                    <span className="text-green-500">-</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={adminFilters.priceMax}
                      onChange={(e) => handleAdminFilterChange('priceMax', e.target.value)}
                      className="w-[70px] h-8 text-center bg-white dark:bg-gray-900"
                      min="0"
                    />
                  </div>
                  
                  {/* Traffic Range Filter */}
                  <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-950 px-3 py-1 rounded-lg border border-purple-200 dark:border-purple-800">
                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">{texts.traffic}:</span>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={adminFilters.trafficMin}
                      onChange={(e) => handleAdminFilterChange('trafficMin', e.target.value)}
                      className="w-[80px] h-8 text-center bg-white dark:bg-gray-900"
                      min="0"
                    />
                    <span className="text-purple-500">-</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={adminFilters.trafficMax}
                      onChange={(e) => handleAdminFilterChange('trafficMax', e.target.value)}
                      className="w-[80px] h-8 text-center bg-white dark:bg-gray-900"
                      min="0"
                    />
                  </div>
                  
                  {/* Sensitive Topics Filter */}
                  <Popover open={sensitiveSectionOpen} onOpenChange={setSensitiveSectionOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className={`h-9 gap-2 ${Object.values(adminFilters).filter((v, i) => ['casino', 'cbd', 'gambling', 'cryptocurrency', 'sex', 'erotic', 'vpn', 'tarot', 'porn', 'cannabis'].includes(Object.keys(adminFilters)[i]) && v).length > 0 ? 'bg-orange-100 border-orange-400' : ''}`}>
                        <span className="text-orange-600">{language === 'es' ? 'Temáticas' : 'Topics'}</span>
                        {[adminFilters.casino, adminFilters.cbd, adminFilters.gambling, adminFilters.cryptocurrency, adminFilters.sex, adminFilters.erotic, adminFilters.vpn, adminFilters.tarot, adminFilters.porn, adminFilters.cannabis].filter(Boolean).length > 0 && (
                          <Badge className="bg-orange-500 text-white text-xs px-1.5">{[adminFilters.casino, adminFilters.cbd, adminFilters.gambling].filter(Boolean).length}</Badge>
                        )}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-3" align="start">
                      <div className="space-y-2">
                        {[
                          { key: 'casino', label: getSensitiveTopicLabel('casino', language) },
                          { key: 'gambling', label: getSensitiveTopicLabel('gambling', language) },
                          { key: 'cbd', label: getSensitiveTopicLabel('cbd', language) },
                        ].map(topic => (
                          <label key={topic.key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded">
                            <Checkbox 
                              checked={adminFilters[topic.key]} 
                              onCheckedChange={() => toggleSensitiveFilter(topic.key)}
                            />
                            <span className="text-sm">{topic.label}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  {(adminFilters.countries.length > 0 || adminFilters.categories.length > 0 || adminFilters.languages.length > 0 || adminFilters.drMin || adminFilters.drMax || adminFilters.trafficMin || adminFilters.trafficMax || adminFilters.priceMin || adminFilters.priceMax || adminFilters.casino || adminFilters.cbd || adminFilters.gambling || productSearch) && (
                    <Badge variant="secondary" className="h-9 px-3 flex items-center">
                      <Filter className="h-3 w-3 mr-1" />
                      {pagination.total.toLocaleString()} {language === 'es' ? 'filtrados' : 'filtered'}
                    </Badge>
                  )}

                  {/* Download Buttons */}
                  <div className="flex items-center gap-2 ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadCatalog(false)}
                      disabled={downloading}
                      className="bg-emerald-50 border-emerald-300 hover:bg-emerald-100"
                    >
                      {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                      {language === 'es' ? 'Descargar Todo' : 'Download All'}
                    </Button>
                    {(adminFilters.countries.length > 0 || adminFilters.categories.length > 0 || adminFilters.languages.length > 0 || adminFilters.drMin || adminFilters.drMax || adminFilters.trafficMin || adminFilters.trafficMax || adminFilters.priceMin || adminFilters.priceMax || adminFilters.casino || adminFilters.cbd || adminFilters.gambling || productSearch) && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleDownloadCatalog(true)}
                        disabled={downloading}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                        {language === 'es' ? `Descargar ${pagination.total.toLocaleString()} Filtrados` : `Download ${pagination.total.toLocaleString()} Filtered`}
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Selected Filters Badges */}
                {(adminFilters.countries.length > 0 || adminFilters.categories.length > 0 || adminFilters.casino || adminFilters.cbd || adminFilters.gambling) && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                    {adminFilters.countries.map(country => (
                      <Badge key={country} variant="secondary" className="px-2 py-1 flex items-center gap-1 bg-emerald-100 text-emerald-800">
                        {translateCountry(country, language)}
                        <button onClick={() => toggleCountry(country)} className="ml-1 hover:bg-emerald-200 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {/* Sensitive Topics Badges - only the 3 with data */}
                    {adminFilters.casino && <Badge className="bg-orange-100 text-orange-800 px-2 py-1 flex items-center gap-1">{getSensitiveTopicLabel('casino', language)} <button onClick={() => toggleSensitiveFilter('casino')} className="ml-1 hover:bg-orange-200 rounded-full p-0.5"><X className="h-3 w-3" /></button></Badge>}
                    {adminFilters.gambling && <Badge className="bg-orange-100 text-orange-800 px-2 py-1 flex items-center gap-1">{getSensitiveTopicLabel('gambling', language)} <button onClick={() => toggleSensitiveFilter('gambling')} className="ml-1 hover:bg-orange-200 rounded-full p-0.5"><X className="h-3 w-3" /></button></Badge>}
                    {adminFilters.cbd && <Badge className="bg-orange-100 text-orange-800 px-2 py-1 flex items-center gap-1">{getSensitiveTopicLabel('cbd', language)} <button onClick={() => toggleSensitiveFilter('cbd')} className="ml-1 hover:bg-orange-200 rounded-full p-0.5"><X className="h-3 w-3" /></button></Badge>}
                    {adminFilters.categories.map(category => (
                      <Badge key={category} variant="secondary" className="px-2 py-1 flex items-center gap-1 bg-blue-100 text-blue-800">
                        {translateCategory(category, language)}
                        <button onClick={() => toggleCategory(category)} className="ml-1 hover:bg-blue-200 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Download Changes Card */}
            <Card className="mb-4 border-primary/30 bg-primary/5">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <History className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">{texts.exportChanges}</h3>
                      <p className="text-sm text-muted-foreground">
                        {language === 'es' 
                          ? 'Descarga un Excel con los cambios realizados a los productos' 
                          : 'Download an Excel with changes made to products'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Select value={String(changeDays)} onValueChange={(v) => setChangeDays(Number(v))}>
                        <SelectTrigger className="w-[130px]">
                          <Calendar className="h-4 w-4 mr-2" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">{texts.today}</SelectItem>
                          <SelectItem value="7">{texts.lastDays} 7 {texts.days}</SelectItem>
                          <SelectItem value="14">{texts.lastDays} 14 {texts.days}</SelectItem>
                          <SelectItem value="30">{texts.lastDays} 30 {texts.days}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleExportChanges} disabled={exportingChanges}>
                      {exportingChanges ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {texts.exportingChanges}
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          {texts.exportChanges}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground">
                  {texts.showing} {products.length} {texts.of} {pagination.total.toLocaleString()}
                </p>
                {selectedProducts.size > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedProducts.size} {texts.selected}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handleSelectAllFromDB} className="border-indigo-300 hover:bg-indigo-50">
                  <Check className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Seleccionar Todos' : 'Select All'} ({pagination.total})
                </Button>
                {selectedProducts.size > 0 && (
                  <>
                    <Button 
                      variant="outline"
                      onClick={() => setSelectedProducts(new Set())}
                    >
                      <X className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'Deseleccionar' : 'Deselect'} ({selectedProducts.size})
                    </Button>
                    <Button 
                      variant="default" 
                      onClick={handleExportSelected}
                      disabled={exportingSelected}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {exportingSelected ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {texts.exportSelected} ({selectedProducts.size})
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => handleExport(true, false)} className="bg-blue-50 hover:bg-blue-100 border-blue-300">
                  <Download className="h-4 w-4 mr-2" />
                  {texts.exportAll} ({pagination.total})
                </Button>
                {(adminFilters.country || adminFilters.category || adminFilters.drMin || adminFilters.drMax || adminFilters.trafficMin || adminFilters.trafficMax || productSearch) && (
                  <Button variant="outline" onClick={() => handleExport(false, true)}>
                    <Filter className="h-4 w-4 mr-2" />
                    {texts.exportFiltered}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDeleteAllDialogOpen(true)} className="text-destructive hover:bg-destructive hover:text-destructive-foreground">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {texts.deleteAll}
                </Button>
                <div className="relative" data-dropdown="column-selector">
                  <Button variant="outline" onClick={() => setShowColumnSelector(!showColumnSelector)}>
                    <Settings className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Columnas' : 'Columns'}
                  </Button>
                  {showColumnSelector && (
                    <div className="absolute right-0 top-full mt-1 w-56 bg-white border rounded-md shadow-lg z-50 p-2 max-h-80 overflow-auto">
                      <div className="text-xs font-medium text-muted-foreground mb-2 px-2">{language === 'es' ? 'Mostrar columnas:' : 'Show columns:'}</div>
                      {[
                        { key: 'email', label: 'Email' },
                        { key: 'country', label: language === 'es' ? 'País' : 'Country' },
                        { key: 'pvp', label: 'PVP' },
                        { key: 'pc', label: 'PC' },
                        { key: 'premiumV', label: 'Prem.V' },
                        { key: 'premiumC', label: 'Prem.C' },
                        { key: 'dr', label: 'DR' },
                        { key: 'traffic', label: language === 'es' ? 'Tráfico' : 'Traffic' },
                        { key: 'available', label: language === 'es' ? 'Disponible' : 'Available' },
                        { key: 'linkType', label: language === 'es' ? 'Tipo Enlace' : 'Link Type' },
                        { key: 'duration', label: language === 'es' ? 'Duración' : 'Duration' },
                        { key: 'label', label: language === 'es' ? 'Etiqueta' : 'Label' },
                        { key: 'sensitive', label: language === 'es' ? 'Temáticas Sensibles' : 'Sensitive Topics' },
                        { key: 'infoPC', label: 'Info PC' },
                        { key: 'secondMail', label: '2º Mail' },
                        { key: 'deleteReason', label: language === 'es' ? 'Motivo Elim.' : 'Del. Reason' },
                      ].map(col => (
                        <button
                          key={col.key}
                          onClick={() => setVisibleColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted rounded"
                        >
                          <Checkbox checked={visibleColumns[col.key]} className="pointer-events-none" />
                          {col.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button onClick={() => openProductDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  {texts.addProduct}
                </Button>
                {selectedProducts.size > 0 && (
                  <Button 
                    variant="destructive" 
                    onClick={handleDeleteSelectedProducts}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {language === 'es' ? `Eliminar (${selectedProducts.size})` : `Delete (${selectedProducts.size})`}
                  </Button>
                )}
              </div>
            </div>

            <AdminProductsTable
              products={products}
              pagination={pagination}
              selectedProducts={selectedProducts}
              visibleColumns={visibleColumns}
              sortField={sortField}
              sortOrder={sortOrder}
              handleSort={handleSort}
              handleSelectAll={handleSelectAll}
              handleSelectProduct={handleSelectProduct}
              openProductDialog={openProductDialog}
              handleProductDelete={handleProductDelete}
              fetchProducts={fetchProducts}
              language={language}
              texts={texts}
            />
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <CRMTab language={language} initialSection="orders" />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <CRMTab language={language} initialSection="customers" onPendingCountChange={refreshPendingCount} />
          </TabsContent>

          {/* Writers Tab */}
          <TabsContent value="writers">
            <CRMTab language={language} initialSection="writers" />
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses">
            <CRMTab language={language} initialSection="expenses" />
          </TabsContent>

          {/* Income Tab */}
          <TabsContent value="income">
            <CRMTab language={language} initialSection="income" />
          </TabsContent>

          {/* Accounting Tab */}
          <TabsContent value="accounting">
            <CRMTab language={language} initialSection="accounting" />
          </TabsContent>

          {/* Import Tab */}
          <TabsContent value="import">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Import Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-primary" />
                    {texts.importTitle}
                  </CardTitle>
                  <CardDescription>{texts.importDescription}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    {selectedFile ? (
                      <div>
                        <p className="font-medium text-foreground">{selectedFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">
                        Arrastra un archivo o haz clic para seleccionar
                      </p>
                    )}
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {texts.selectFile}
                    </Button>
                  </div>

                  <div className="flex items-start space-x-2 p-3 bg-warning/10 rounded-lg">
                    <Checkbox
                      id="replaceAll"
                      checked={replaceAll}
                      onCheckedChange={setReplaceAll}
                    />
                    <div className="space-y-1">
                      <Label htmlFor="replaceAll" className="cursor-pointer font-medium">
                        {texts.replaceAll}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {texts.replaceAllWarning}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      onClick={handleImport}
                      disabled={!selectedFile || importing}
                    >
                      {importing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {texts.importing}
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          {texts.importButton}
                        </>
                      )}
                    </Button>
                    <Button variant="outline" onClick={handleDownloadTemplate}>
                      <Download className="h-4 w-4 mr-2" />
                      {texts.downloadTemplate}
                    </Button>
                  </div>

                  {/* Import progress */}
                  {importing && (
                    <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="import-progress">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-blue-700">{importStatusMessage}</span>
                        <span className="text-blue-700">{importProgress}%</span>
                      </div>
                      <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full transition-all duration-300"
                          style={{ width: `${Math.min(100, Math.max(0, importProgress))}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Import Result */}
                  {importResult && (
                    <Alert variant={importResult.errors > 0 ? 'destructive' : 'default'}>
                      {importResult.errors > 0 ? (
                        <AlertCircle className="h-4 w-4" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                      <AlertTitle>{texts.importSuccess}</AlertTitle>
                      <AlertDescription>
                        <p>
                          {importResult.imported} {texts.importedRows}, {importResult.errors}{' '}
                          {texts.errorRows}
                        </p>
                        {importResult.error_details?.length > 0 && (
                          <ul className="mt-2 text-sm list-disc list-inside">
                            {importResult.error_details.slice(0, 5).map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                            {importResult.error_details.length > 5 && (
                              <li>...y {importResult.error_details.length - 5} más</li>
                            )}
                          </ul>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Export Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="h-5 w-5 text-primary" />
                    {texts.export}
                  </CardTitle>
                  <CardDescription>
                    Descarga el catálogo de productos en formato Excel
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-6 bg-muted/50 rounded-lg text-center">
                    <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-2xl font-bold">{stats?.total_products?.toLocaleString() || 0}</p>
                    <p className="text-muted-foreground">productos en el catálogo</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport(true)}>
                      <Download className="h-6 w-6 mb-2" />
                      {texts.exportAll}
                    </Button>
                    <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport(false)}>
                      <FileSpreadsheet className="h-6 w-6 mb-2" />
                      {texts.exportPage}
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground text-center">
                    El archivo se descargará en formato Excel (.xlsx)
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Excel Tools Section */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Clean Excel Tool */}
              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-600">
                    <Wand2 className="h-5 w-5" />
                    {language === 'es' ? 'Limpiar Excel' : 'Clean Excel'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'es' 
                      ? 'Sube un Excel con cualquier formato y conviértelo al formato correcto para importar productos'
                      : 'Upload any Excel format and convert it to the correct format for importing products'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <input
                    type="file"
                    id="cleanExcelInput"
                    accept=".xlsx,.xls"
                    className="hidden"
                    disabled={cleaningExcel}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setCleaningExcel(true);
                      setTaskProgress(prev => ({ ...prev, clean: 0 }));
                      setTaskMessage(prev => ({ ...prev, clean: language === 'es' ? 'Iniciando...' : 'Starting...' }));
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        // Start background task
                        const response = await axios.post(`${API}/tasks/clean-excel/start`, formData);
                        
                        if (response.data.task_id) {
                          setCleanExcelTaskId(response.data.task_id);
                          toast.info(language === 'es' ? 'Procesando en segundo plano...' : 'Processing in background...');
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al iniciar proceso' : 'Error starting process'));
                        setCleaningExcel(false);
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button 
                    variant="outline" 
                    className="w-full h-24 flex-col border-dashed border-2 border-purple-300 hover:border-purple-500 hover:bg-purple-50"
                    onClick={() => !cleaningExcel && document.getElementById('cleanExcelInput').click()}
                    disabled={cleaningExcel}
                  >
                    {cleaningExcel ? (
                      <>
                        <Loader2 className="h-8 w-8 mb-2 text-purple-500 animate-spin" />
                        <span className="text-purple-700 font-medium">
                          {taskMessage.clean || (language === 'es' ? 'Procesando...' : 'Processing...')}
                        </span>
                        {taskProgress.clean > 0 && (
                          <span className="text-xs text-purple-500 mt-1">{taskProgress.clean}%</span>
                        )}
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mb-2 text-purple-500" />
                        <span className="text-purple-700 font-medium">
                          {language === 'es' ? 'Subir Excel para Limpiar' : 'Upload Excel to Clean'}
                        </span>
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {language === 'es' 
                      ? 'El sistema detectará las columnas y las renombrará al formato estándar'
                      : 'The system will detect columns and rename them to the standard format'}
                  </p>
                </CardContent>
              </Card>

              {/* Detect Changes Tool */}
              <Card className="border-cyan-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-cyan-600">
                    <Clock className="h-5 w-5" />
                    {language === 'es' ? 'Detectar Cambios (72h)' : 'Detect Changes (72h)'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'es'
                      ? 'Compara un Excel con los productos actuales y detecta cambios en las últimas 72 horas'
                      : 'Compare an Excel with current products and detect changes in the last 72 hours'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <input
                    type="file"
                    id="detectChangesInput"
                    accept=".xlsx,.xls"
                    className="hidden"
                    disabled={detectingChanges}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setDetectingChanges(true);
                      setTaskProgress(prev => ({ ...prev, detect: 0 }));
                      setTaskMessage(prev => ({ ...prev, detect: language === 'es' ? 'Iniciando...' : 'Starting...' }));
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        // Start background task
                        const response = await axios.post(`${API}/tasks/detect-changes/start`, formData);
                        
                        if (response.data.task_id) {
                          setDetectChangesTaskId(response.data.task_id);
                          toast.info(language === 'es' ? 'Analizando en segundo plano...' : 'Analyzing in background...');
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al iniciar análisis' : 'Error starting analysis'));
                        setDetectingChanges(false);
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button 
                    variant="outline" 
                    className="w-full h-24 flex-col border-dashed border-2 border-cyan-300 hover:border-cyan-500 hover:bg-cyan-50"
                    onClick={() => !detectingChanges && document.getElementById('detectChangesInput').click()}
                    disabled={detectingChanges}
                  >
                    {detectingChanges ? (
                      <>
                        <Loader2 className="h-8 w-8 mb-2 text-cyan-500 animate-spin" />
                        <span className="text-cyan-700 font-medium">
                          {taskMessage.detect || (language === 'es' ? 'Analizando...' : 'Analyzing...')}
                        </span>
                        {taskProgress.detect > 0 && (
                          <span className="text-xs text-cyan-500 mt-1">{taskProgress.detect}%</span>
                        )}
                      </>
                    ) : (
                      <>
                        <Search className="h-8 w-8 mb-2 text-cyan-500" />
                        <span className="text-cyan-700 font-medium">
                          {language === 'es' ? 'Subir Excel para Comparar' : 'Upload Excel to Compare'}
                        </span>
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {language === 'es'
                      ? 'Genera un reporte con: nuevos, eliminados y modificados en 72h'
                      : 'Generates a report with: new, deleted and modified in 72h'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Column Reference */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Columnas del Excel</CardTitle>
                <CardDescription>
                  El archivo Excel debe contener las siguientes columnas (el orden no importa):
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                  {[
                    'Website',
                    'Pais',
                    'Categoria',
                    'Casino',
                    'Cbd',
                    'Gambling',
                    'Etiqueta',
                    'Precio Venta',
                    'Precio material sensible',
                    'Mas informacion',
                    'Available',
                    'DR',
                    'Trafico organico ahrefs',
                    'Idioma',
                    'Periodico, web o blog',
                    'Tipo de enlace',
                    'Duracion de enlace',
                  ].map((col) => (
                    <div key={col} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                      <span>{col}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="space-y-6">
              {/* Add Category Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-primary" />
                    {language === 'es' ? 'Añadir Nueva Categoría' : 'Add New Category'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Input
                      placeholder={language === 'es' ? 'Nombre de la categoría...' : 'Category name...'}
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="flex-1"
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                    />
                    <Select value={newCategoryType} onValueChange={setNewCategoryType}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">{language === 'es' ? 'Regular' : 'Regular'}</SelectItem>
                        <SelectItem value="sensitive">{language === 'es' ? 'Sensible' : 'Sensitive'}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleCreateCategory}>
                      <Plus className="h-4 w-4 mr-1" />
                      {language === 'es' ? 'Añadir' : 'Add'}
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" className="mt-4" onClick={handleSeedCategories}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Cargar categorías predeterminadas' : 'Load default categories'}
                  </Button>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Regular Categories */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-emerald-600">
                      <FolderOpen className="h-5 w-5" />
                      {language === 'es' ? 'Categorías Regulares' : 'Regular Categories'}
                      <Badge variant="secondary">{dbCategories.regular.length}</Badge>
                    </CardTitle>
                    <CardDescription>
                      {language === 'es' ? 'Categorías estándar para productos' : 'Standard categories for products'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {dbCategories.regular.map(cat => (
                        <div key={cat.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <span className="font-medium text-emerald-800">{cat.name}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" 
                            onClick={() => handleDeleteCategory(cat.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {dbCategories.regular.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">
                          {language === 'es' ? 'Sin categorías regulares' : 'No regular categories'}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Sensitive Categories */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-orange-600">
                      <AlertTriangle className="h-5 w-5" />
                      {language === 'es' ? 'Categorías Sensibles' : 'Sensitive Categories'}
                      <Badge variant="secondary" className="bg-orange-100 text-orange-700">{dbCategories.sensitive.length}</Badge>
                    </CardTitle>
                    <CardDescription>
                      {language === 'es' ? 'Categorías para contenido sensible (casino, CBD, etc.)' : 'Categories for sensitive content (casino, CBD, etc.)'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {dbCategories.sensitive.map(cat => (
                        <div key={cat.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <span className="font-medium text-orange-800">{cat.name}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" 
                            onClick={() => handleDeleteCategory(cat.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {dbCategories.sensitive.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">
                          {language === 'es' ? 'Sin categorías sensibles' : 'No sensitive categories'}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsDashboard token={localStorage.getItem('auth_token')} />
          </TabsContent>

          {/* System Health Tab */}
          <TabsContent value="health">
            <SystemHealthDashboard token={localStorage.getItem('auth_token')} />
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <AuditLogDashboard token={localStorage.getItem('auth_token')} language={language} />
          </TabsContent>

          {/* Frontend Errors Tab */}
          <TabsContent value="errors">
            <FrontendErrorsDashboard token={localStorage.getItem('auth_token')} />
          </TabsContent>
          
          <TabsContent value="media-search">
            <MediaSearchTab />
          </TabsContent>

          <TabsContent value="growwer">
            <GrowwerSyncTab />
          </TabsContent>

          <TabsContent value="email-tool">
            <EmailToolTab />
          </TabsContent>

          <TabsContent value="supervisor">
            <SupervisorTab />
          </TabsContent>

          <TabsContent value="api-keys">
            <ApiKeysTab language={language} />
          </TabsContent>
        </Tabs>

        {/* Product Dialog */}
        <ProductFormDialog
          open={productDialogOpen}
          onOpenChange={setProductDialogOpen}
          editingProduct={editingProduct}
          productForm={productForm}
          setProductForm={setProductForm}
          onSave={handleProductSave}
          dbCategories={dbCategories}
          setCategoryDialogOpen={setCategoryDialogOpen}
          language={language}
          texts={texts}
        />
        
        
        {/* Create User Dialog */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{texts.createUser}</DialogTitle>
              <DialogDescription>
                {language === 'es' ? 'Crea un nuevo usuario para acceder al sistema' : 'Create a new user to access the system'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="userName">{texts.name}</Label>
                <Input
                  id="userName"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder={language === 'es' ? 'Nombre completo' : 'Full name'}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userEmail">{texts.email}</Label>
                <Input
                  id="userEmail"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userPassword">{texts.password}</Label>
                <Input
                  id="userPassword"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="userIsAdmin"
                  checked={userForm.is_admin}
                  onCheckedChange={(checked) => setUserForm({ ...userForm, is_admin: checked })}
                />
                <Label htmlFor="userIsAdmin">{texts.isAdmin}</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
                {texts.cancel}
              </Button>
              <Button onClick={handleCreateUser} disabled={creatingUser} className="bg-emerald-600 hover:bg-emerald-700">
                {creatingUser ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                {texts.createUser}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Management Dialog - Using extracted component */}
        <CategoryManager 
          open={categoryDialogOpen} 
          onOpenChange={setCategoryDialogOpen} 
          language={language} 
        />
        
        {/* Delete All Confirmation Dialog */}
        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                {texts.confirmDeleteAllTitle}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {texts.confirmDeleteAllDesc}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label className="text-sm font-medium">{texts.typeToConfirm}</Label>
              <Input
                className="mt-2"
                placeholder={texts.deleteWord}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setDeleteConfirmText(''); }}>
                {texts.cancel}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllProducts}
                disabled={deleteConfirmText !== texts.deleteWord}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {texts.deleteAll}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
};

export default AdminPage;
